"use client";

import React, { Fragment, useState, useCallback, useEffect } from "react";
import {
  encontrarConflitosCategoriaLiga,
  mensagemConflito,
  type CategoriaLigaRef,
  type InscricaoExistenteComLiga,
} from "../utils/exclusividadeCategoriaLiga";
import { formatPeriodoTorneioBR } from "../utils/date";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { Chip, Avatar, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';

import Header from "../components/Header";
import Appfooter from "../components/Appfooter";
import ProfileCardTournament from "../components/ProfilecardTournament";
import RegulamentoModal from "../components/RegulamentoModal";
import { podeSelecionarCategoria } from "../utils/filaEspera";
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
  CategoriaTorneio,
  CONTAGEM_VAGAS_POR_CATEGORIA,
  ContagemVagasData,
  ContagemVagasVars,
} from "../services/torneioService";
import SelectPartnerModal, { Partner } from "../components/SelectPartnerModal";
import Load from "../components/Load";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";

import PartnerChip from "../components/PartnerChip";
import TournamentTabs, { TournamentTabId } from "../components/TournamentTabs";
import TournamentSidebar from "../components/TournamentSidebar";
import TournamentShortcuts from "../components/TournamentShortcuts";
import LocalizacaoMapaView from "../components/local/LocalizacaoMapaView";
import TournamentCategoriasTab from "../components/TournamentCategoriasTab";
import TournamentSeguidoresTab from "../components/TournamentSeguidoresTab";
import { useSeguirTorneio } from "../hooks/useSeguirTorneio";
import {
  SEGUIDORES_POR_TORNEIO,
  SeguidoresPorTorneioData,
  SeguidoresPorTorneioVars,
} from "../services/seguidorService";
import Createpost from "../components/Createpost";
import PostRouter from "../components/PostRouter";
import { listarPostsPorTorneio, Post } from "../services/postService";
import { computeIdentidadesFeed } from "../utils/identidadesFeed";

import {
  INSCRICOES_ATLETA_TORNEIO,
  InscricoesAtletaTorneioData,
  InscricoesAtletaTorneioVars,
} from "../services/inscricaoTorneioService";
import { getVagasInfo } from "../utils/vagasUtils";

// Utilitário de cálculo de totais com descontos por categoria
type TipoDesconto = "FIXO" | "PORCENTAGEM" | "PERCENTUAL";

interface DescontoCategoriaCalc {
  id?: string;
  ordem?: number; // usa ordem (ou ordemAplicacao) quando existir
  valor: number;
  tipo: TipoDesconto;
}

interface CategoriaSelecionadaCalc {
  id: string;
  nome: string;
  precoInscricao: number;
  aplicarDescontoTorneio: boolean;
}

function computeInscriptionTotals(
  selected: CategoriaSelecionadaCalc[],
  descontos: DescontoCategoriaCalc[],
  offset: number = 0) {
  const round2 = (n: number) => Math.round(n * 100) / 100;

  const valorSemDesconto = round2(
    selected.reduce((acc, c) => acc + (c.precoInscricao || 0), 0),
  );

  const elegiveis = selected.filter((c) => c.aplicarDescontoTorneio);
  const descontosOrdenados = [...descontos].sort(
    (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0),
  );

  let totalDesconto = 0;

  // aplicar descontos na mesma ordem das elegíveis
  elegiveis.forEach((cat, idx) => {
    const preco = cat.precoInscricao || 0;
    const d = descontosOrdenados[offset + idx];
    if (!d) return;
    const tipo = String((d as any).tipo || '').toUpperCase();
    const isPct = tipo === "PORCENTAGEM" || tipo === "PERCENTUAL";
    const desconto = isPct
      ? round2(preco * (d.valor / 100))
      : round2(Math.min(d.valor, preco));
    totalDesconto = round2(totalDesconto + desconto);
  });

  const valorComDesconto = round2(valorSemDesconto - totalDesconto);
  return {
    valorSemDesconto,
    descontoAplicado: totalDesconto,
    valorComDesconto,
  };
}

const Item = styled(Paper)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: (theme.vars ?? theme).palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
}));

const Tournamentsingle: React.FC = () => {
  const [regulamentoOpen, setRegulamentoOpen] = useState<boolean>(false);
  const [selectPartnerOpen, setSelectPartnerOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<{
    categoria: CategoriaTorneio;
    partner?: Partner;
  }[]>([]);
  const [currentCategory, setCurrentCategory] = useState<CategoriaTorneio | null>(null);
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistCategories, setWaitlistCategories] = useState<string[]>([]);
  const [conflitoDialogOpen, setConflitoDialogOpen] = useState(false);
  const [conflitosCategoriaLiga, setConflitosCategoriaLiga] = useState<string[]>([]);
  const [regulamentoAceito, setRegulamentoAceito] = useState<boolean>(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const user =
    (auth?.user as any) ||
    (typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem('user') || 'null')
      : null);

  const isAdmin = user?.role === 'ADMIN';
  const { currentProfile } = useProfile();
  const isAtleta = currentProfile?.tipo === 'ATLETA';
  const seguidorUserId: string = user?.atleta?.id ?? '';

  const {
    isFollowing,
    loading: followLoading,
    handleSeguir,
  } = useSeguirTorneio({
    seguidorUserId,
    torneioId: id || '',
    skip: !seguidorUserId || !id,
  });

  // Hash-based tab state management
  const location = useLocation();

  const hashToTab = useCallback((hash: string): TournamentTabId => {
    if (hash === '#inscreva-se') return 'inscreva-se';
    if (hash === '#categorias') return 'categorias';
    if (hash === '#seguidores') return 'seguidores';
    return 'principal';
  }, []);

  const [activeTab, setActiveTabState] = useState<TournamentTabId>(() =>
    hashToTab(location.hash)
  );

  useEffect(() => {
    setActiveTabState(hashToTab(location.hash));
  }, [location.hash, hashToTab]);

  const onTabChange = useCallback((tab: TournamentTabId) => {
    setActiveTabState(tab);
    if (typeof window !== 'undefined') {
      if (tab === 'principal') {
        window.history.replaceState(null, '', location.pathname);
      } else {
        window.history.replaceState(null, '', `${location.pathname}#${tab}`);
      }
    }
  }, [location.pathname]);

  const { data, loading, error } = useQuery<TorneioPorIdData, TorneioPorIdVars>(
    TORNEIO_POR_ID,
    {
      variables: { id: id || "" },
      skip: !id,
    }
  );

  const torneio = data?.torneioPorId;
  const regulamentoHtml = torneio?.regulamento || "";

  const { identidades, canPost } = torneio
    ? computeIdentidadesFeed({
        torneio,
        userId: user?.id ?? null,
        atletaId: user?.atleta?.id ?? null,
        atletaNome: user?.nome ?? null,
        atletaImagemUrl: user?.imagemUrl ?? null,
        isAdmin,
        userOrganizadores: user?.organizadores ?? null,
      })
    : { identidades: [], canPost: false };

  // Na página do torneio, publicações só podem ser feitas com o perfil do torneio
  const identidadesPost = identidades.filter(i => i.tipo === 'TORNEIO');

  const { data: seguidoresData, loading: seguidoresLoading, error: seguidoresError } = useQuery<
    SeguidoresPorTorneioData,
    SeguidoresPorTorneioVars
  >(SEGUIDORES_POR_TORNEIO, {
    variables: { torneioId: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const seguidores = seguidoresData?.seguidoresPorTorneioId ?? [];
  const seguidoresErrorMsg = seguidoresError?.message ?? null;

  const handleCategoryClick = (categoriaId: string) => {
    navigate(`/tournament/${id}/category/${categoriaId}`);
  };

  const handlePagarInscricao = (pagamentoId: string) => {
    navigate(`/athlete/tournamentinscriptions/${pagamentoId}`);
  };

  const handleCategoryChange = (cat: CategoriaTorneio, checked: boolean) => {
    setSelectedCategories((prev) => {
      if (checked) {
        return [...prev, { categoria: cat }];
      }
      return prev.filter((c) => c.categoria.id !== cat.id);
    });
  };

  const openPartnerModal = (cat: CategoriaTorneio) => {
    setCurrentCategory(cat);
    setSelectPartnerOpen(true);
  };

  const closePartnerModal = () => {
    setSelectPartnerOpen(false);
    setCurrentCategory(null);
  };

  const handleSelectPartner = (partner: Partner) => {
    if (!currentCategory) return;
    setSelectedCategories((prev) =>
      prev.map((c) =>
        c.categoria.id === currentCategory.id ? { ...c, partner } : c
      )
    );
    closePartnerModal();
  };

  const removePartner = (catId: string) => {
    setSelectedCategories((prev) =>
      prev.map((c) =>
        c.categoria.id === catId ? { ...c, partner: undefined } : c
      )
    );
  };

  const atletaId = user?.atleta?.id ?? '';
  const atletaNome = user?.nome ?? '';

  // Feed state for Principal tab
  const [posts, setPosts] = useState<Post[]>([]);
  const [feedPage, setFeedPage] = useState(0);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  const curtidorAtletaId = identidades.find(i => i.tipo === 'ATLETA')?.id || atletaId;
  const curtidorTorneioId = identidades.find(i => i.tipo === 'TORNEIO')?.id || id || '';
  const curtidorLigaId = identidades.find(i => i.tipo === 'LIGA')?.id || '';

  const loadPosts = useCallback(async (pageToLoad: number, append = false) => {
    if (!id) return;
    setPostsLoading(true);
    setPostsError(null);
    try {
      const result = await listarPostsPorTorneio(id, pageToLoad, pageSize, curtidorAtletaId, curtidorTorneioId, curtidorLigaId);
      const items = result.items || [];
      setPosts((prev) => (append ? [...prev, ...items] : items));
      setHasMore(pageToLoad < (result.totalPages - 1));
    } catch (err) {
      setPostsError('Erro ao carregar posts.');
    } finally {
      setPostsLoading(false);
    }
  }, [id, curtidorAtletaId, curtidorTorneioId, curtidorLigaId]);

  useEffect(() => { loadPosts(0); }, [loadPosts]);

  const handlePostCreated = useCallback(async () => {
    setFeedPage(0);
    await loadPosts(0);
  }, [loadPosts]);

  const handleLoadMore = useCallback(() => {
    const nextPage = feedPage + 1;
    loadPosts(nextPage, true);
    setFeedPage(nextPage);
  }, [feedPage, loadPosts]);

  const { data: inscricoesData } = useQuery<
    InscricoesAtletaTorneioData,
    InscricoesAtletaTorneioVars
  >(INSCRICOES_ATLETA_TORNEIO, {
    variables: { atletaId, torneioId: id || '' },
    skip: !atletaId || !id,
    fetchPolicy: 'cache-and-network',
  });

  let inscricoes = inscricoesData?.inscricoesTorneioPorAtletaIdTorneioId || [];
  inscricoes = inscricoes.filter((i) => i.status != 'CANCELADA' );

  // Fetch lightweight vagas count per category
  const { data: contagemVagasData } = useQuery<
    ContagemVagasData,
    ContagemVagasVars
  >(CONTAGEM_VAGAS_POR_CATEGORIA, {
    variables: { torneioId: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const contagemVagas = contagemVagasData?.contagemVagasPorCategoria || [];

  // Mapear categorias do torneio para checar elegibilidade a desconto
  const categoriasMap = new Map(
    (torneio?.categoriasTorneio ?? []).map((c) => [c.id, c]),
  );

  // Contar quantas inscrições prévias do atleta são elegíveis a desconto
  const priorEligibleCount = (inscricoes ?? []).reduce((acc: number, ins: any) => {
    const catId = ins?.categoriaTorneio?.id;
    const cat = catId ? categoriasMap.get(catId) : undefined;
    return acc + (cat?.aplicarDescontoTorneio ? 1 : 0);
  }, 0);

  const canSubmit =
    selectedCategories.length > 0 &&
    selectedCategories.every(
      (c) => c.categoria.numeroAtletas === null || c.categoria.numeroAtletas === 1 || !!c.partner
    ) &&
    regulamentoAceito;

  const proceedToCheckout = () => {
    // Preparar valores totais com desconto para trafegar ao checkout
    const descontos = (torneio?.descontoCategorias ?? []) as unknown as DescontoCategoriaCalc[];
    const selecionadas: CategoriaSelecionadaCalc[] = selectedCategories.map(({ categoria }) => ({
      id: categoria.id,
      nome: categoria.nome,
      precoInscricao: categoria.precoInscricao ?? 0,
      aplicarDescontoTorneio: Boolean((categoria as any).aplicarDescontoTorneio),
    }));
    const totals = computeInscriptionTotals(selecionadas, descontos, priorEligibleCount);

    const inscricoes = selectedCategories.map((c) => ({
      categoria: {
        id: c.categoria.id,
        nome: c.categoria.nome,
        valor: c.categoria.precoInscricao ?? 0,
      },
      equipe: [
        { id: atletaId, nome: atletaNome },
        ...(c.partner ? [{ id: c.partner.id, nome: c.partner.nome }] : []),
      ],
    }));
    navigate('/tournamentinscriptioncheckout', {
      state: {
        inscricoes,
        torneioId: id as string,
        valorSemDesconto: totals.valorSemDesconto,
        descontoAplicado: totals.descontoAplicado,
        valorComDesconto: totals.valorComDesconto,
      },
    });
  };

  const handleSubmit = () => {
    // Exclusividade entre CategoriaLiga (PRD nivel-categoria-liga): barra ANTES de avancar
    // para o checkout. Vem antes do dialogo de fila de espera de proposito — fila e' uma
    // CONFIRMACAO ("deseja continuar?"), conflito e' um BLOQUEIO; perguntar sobre a fila
    // de uma inscricao que nem pode existir seria pedir decisao sobre algo impossivel.
    const conflitos = encontrarConflitosCategoriaLiga(
      selectedCategories.map((c) => ({
        id: c.categoria.id,
        nome: c.categoria.nome,
        categoriaLiga: (c.categoria as unknown as { categoriaLiga?: CategoriaLigaRef | null })
          .categoriaLiga,
      })),
      inscricoes as unknown as InscricaoExistenteComLiga[],
    );
    if (conflitos.length > 0) {
      setConflitosCategoriaLiga(conflitos.map(mensagemConflito));
      setConflitoDialogOpen(true);
      return;
    }

    // Check if any selected category will go to waitlist
    const categoriasEmFila = selectedCategories
      .filter((c) => {
        const info = getVagasInfo(c.categoria.id, c.categoria.vagas, contagemVagas, torneio?.filaEspera, c.categoria.configFilaEspera);
        return info.status === 'waitlist';
      })
      .map((c) => c.categoria.nome);

    if (categoriasEmFila.length > 0) {
      setWaitlistCategories(categoriasEmFila);
      setWaitlistDialogOpen(true);
      return;
    }

    proceedToCheckout();
  };

  return (
    <Fragment>
      <Header />

      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="row justify-content-center">
              <div className="col-xl-10 col-xxl-10 col-lg-10">
                {loading && <Load />}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Erro ao carregar torneio.
                  </div>
                )}
                {torneio && <ProfileCardTournament torneio={torneio} />}

                {torneio && (
                  <TournamentTabs
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    principalContent={
                      <div className="row">
                        {/* Coluna Esquerda - Sidebar */}
                        <TournamentSidebar
                          torneio={torneio}
                          onInscreva={() => onTabChange('inscreva-se')}
                          isFollowing={isFollowing}
                          followLoading={followLoading}
                          onFollow={seguidorUserId ? handleSeguir : undefined}
                        />

                        {/* Coluna Central - Feed */}
                        <div className="col-xl-6">
                          {canPost && torneio && (
                            <Createpost
                              identidades={identidadesPost}
                              perfilAtivo={currentProfile}
                              onPostCreated={handlePostCreated}
                            />
                          )}
                          {postsLoading && <Load />}
                          {postsError && (
                            <div className="alert alert-danger" role="alert">{postsError}</div>
                          )}
                          {!postsLoading && posts.length === 0 && (
                            <div className="alert alert-info" role="alert">
                              Nenhum post encontrado para este torneio.
                            </div>
                          )}
                          {posts.map((post) => (
                            <PostRouter
                              key={post.id}
                              post={post}
                              torneioId={id}
                              identidades={identidades}
                              perfilAtivo={currentProfile}
                            />
                          ))}
                          {hasMore && posts.length > 0 && (
                            <div className="text-center my-3">
                              <button className="btn btn-primary" onClick={handleLoadMore}>
                                Carregar mais
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Coluna Direita - Shortcuts */}
                        <TournamentShortcuts
                          torneio={torneio}
                          onInscreva={() => onTabChange('inscreva-se')}
                        />
                      </div>
                    }
                    categoriasContent={
                      <TournamentCategoriasTab
                        categorias={torneio.categoriasTorneio || []}
                        torneioId={id!}
                        onInscreva={() => onTabChange('inscreva-se')}
                        onCategoryClick={handleCategoryClick}
                        inscricoes={inscricoes}
                        contagemVagas={contagemVagas}
                        isAtleta={isAtleta}
                        onPagarInscricao={handlePagarInscricao}
                        exibirFilaEspera={torneio?.exibirFilaEspera}
                        exibirInscritos={torneio?.exibirInscritos}
                      />
                    }
                    seguidoresContent={
                      <TournamentSeguidoresTab
                        seguidores={seguidores}
                        loading={seguidoresLoading}
                        error={seguidoresErrorMsg}
                      />
                    }
                    inscrevaSeContent={
                      <div className="row">
                        <div className="col-xl-12 col-xxl-12 col-lg-12 card-body">
                          <div className="row">
                            <div className="col-xl-1">
                              <figure className="avatar w50 me-auto">
                                <img
                                  src={torneio?.avatarUrl || "/assets/images/user.png"}
                                  alt={torneio?.nome || "avatar"}
                                  className="p-1 bg-white rounded-xl w-100"
                                />
                              </figure>
                            </div>
                            <div className="col-xl-11">
                              <h5 id='subtitle-tournament' className="font-xs ls-1 fw-700 text-grey-900">
                                {torneio?.subtitulo || torneio?.nome}
                                <span id='date-tournament' className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
                                  {formatPeriodoTorneioBR(torneio?.dataInicio, torneio?.dataFim)}{" "}
                                  {torneio?.local?.endereco?.cidade && (
                                    <span id='city-uf-tournament' className="text-primary">
                                      {torneio.local.endereco.cidade.nome},{torneio.local.endereco.cidade.uf}
                                    </span>
                                  )}
                                </span>
                              </h5>
                            </div>
                            <hr />
                          </div>
                        </div>
                        <div className="col-xl-6 col-xxl-6 col-lg-6 card-body">
                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                            Informações do torneio
                          </h4>
                          <hr />
                          {torneio?.descricao && (
                            <div
                              dangerouslySetInnerHTML={{ __html: torneio.descricao }}
                            />
                          )}
                          <div className="d-flex justify-content-start mt-4">
                            <button
                              id="regulamento-button"
                              type="button"
                              className="btn btn-light d-flex align-items-center px-4 py-2 shadow-sm mr-auto"
                              style={{
                                backgroundColor: "#bbbbbb",
                                color: "#333",
                                border: "1px solid #000",
                                borderRadius: "8px",
                              }}
                              onClick={() => setRegulamentoOpen(true)}
                            >
                              <i
                                className="bi bi-file-earmark-text me-2"
                                style={{ fontSize: "1.2rem" }}
                              ></i>
                              <span className="fw-600">REGULAMENTO</span>
                            </button>
                          </div>
                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3 mt-4">
                            Endereço
                          </h4>
                          <hr />
                          <p className=" font-xssss fw-500 mt-1 lh-3">
                            <i className="bi bi-geo-alt-fill text-primary me-2" />
                            <strong>Local:</strong> {torneio?.local?.nome}
                            <br />
                            {torneio?.local?.endereco && (
                              <span>
                                {torneio.local?.endereco?.logradouro}, {torneio.local?.endereco?.numero}
                                {torneio.local?.endereco?.complemento ? ` ${torneio.local?.endereco?.complemento}` : ''} - {torneio.local?.endereco?.bairro}, {torneio.local?.endereco?.cidade?.nome}-{torneio.local?.endereco?.cidade?.uf} {torneio.local?.endereco?.cep}
                              </span>
                            )}
                          </p>
                          <LocalizacaoMapaView urlLocalizacao={torneio?.local?.urlLocalizacao} />

                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                            Descontos por categoria
                          </h4>
                          <hr />
                          {(torneio?.descontosCategoria?.length ?? 0) === 0 ? (
                            <p className="font-xssss fw-500 mt-1 lh-3">Nenhum desconto cadastrado.</p>
                          ) : (
                            <ul className="font-xssss fw-500 mt-1 lh-3 list-unstyled">
                              {[...(torneio?.descontosCategoria ?? [])]
                                .sort((a: any, b: any) => (a.ordem ?? 0) - (b.ordem ?? 0))
                                                          .map((d: any, idx: number) => {
                                  const prefixo = `${idx + 1}\u00BA desconto: `;
                                  const textoValor =
                                    (d.tipo === "PORCENTAGEM" || d.tipo === "PERCENTUAL")
                                      ? `${Number(d.valor).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
                                      : `R$ ${Number(d.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                  return (
                                    <li key={d.id}>
                                      <strong>{prefixo}{textoValor}</strong>
                                      {d.descricao ? ` - ${d.descricao}` : ""}
                                    </li>
                                  );
                                })}
                            </ul>
                          )}
                        </div>
                        <div className="col-xl-6 col-xxl-6 col-lg-6 card-body">
                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                            Organização do Torneio
                          </h4>
                          <hr />
                          {loading ? (
                            <div className="d-flex justify-content-center py-3">
                              <CircularProgress size={24} />
                            </div>
                          ) : error ? (
                            <Typography color="error" variant="body2">
                              Não foi possível carregar os organizadores.
                            </Typography>
                          ) : torneio?.organizadores && torneio.organizadores.length > 0 ? (
                            <Grid container spacing={2}>
                              {torneio.organizadores.map((org) => (
                                <Item key={org.id} id={org.id}>
                                  <div className="d-flex align-items-center justify-content-between">
                                    <Avatar
                                      src={org.fotoUrl || org.usuario?.imagemUrl || "/assets/images/user.png"}
                                      alt={`Organizador: ${org.nome}`}
                                      className="me-2"
                                    />
                                    <div>
                                      <Typography variant="body2" noWrap>
                                        {org.nome}
                                      </Typography>
                                      {org.usuario?.username && (
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                          @{org.usuario.username}
                                        </Typography>
                                      )}
                                      {org.telefone && (
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                          {org.telefone}
                                        </Typography>
                                      )}
                                    </div>
                                     <a
                                        href={`https://wa.me/${org.telefone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn d-flex align-items-center px-4 py-2"
                                        style={{
                                          backgroundColor: '#25D366',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '8px',
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                        }}
                                      >
                                        <i className="bi bi-whatsapp me-2" style={{ fontSize: '1.4rem' }}></i>
                                        <span className="fw-600">Mensagem</span>
                                      </a>
                                  </div>
                                </Item>
                              ))}
                            </Grid>
                          ) : (
                            <Typography variant="body2">
                              Nenhum organizador cadastrado.
                            </Typography>
                          )}
                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3 mt-4">
                            Patrocinadores
                          </h4>
                          <hr />
                          {loading ? (
                            <div className="d-flex justify-content-center py-3">
                              <CircularProgress size={24} />
                            </div>
                          ) : error ? (
                            <Typography color="error" variant="body2">
                              Não foi possível carregar os patrocinadores.
                            </Typography>
                          ) : torneio?.patrocinadores && torneio.patrocinadores.length > 0 ? (
                            <Grid container spacing={2}>
                              {torneio.patrocinadores.map((patrocinador) => (
                                <Item key={patrocinador.id}>
                                  <div className="d-flex flex-column align-items-center text-center">
                                    <Avatar
                                      src={
                                        patrocinador.fotoUrl ||
                                        '/assets/images/logo-placeholder.png'
                                      }
                                      alt={patrocinador.nome}
                                      sx={{ width: 64, height: 64, mb: 1 }}
                                    />
                                    <Typography variant="body2" className="fw-600">
                                      {patrocinador.nome}
                                    </Typography>
                                    {patrocinador.descricao && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        className="mt-1"
                                      >
                                        {patrocinador.descricao}
                                      </Typography>
                                    )}
                                    {patrocinador.siteUrl && (
                                      <a
                                        href={patrocinador.siteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary mt-2"
                                      >
                                        Visitar site
                                      </a>
                                    )}
                                  </div>
                                </Item>
                              ))}
                            </Grid>
                          ) : (
                            <Typography variant="body2">
                              Nenhum patrocinador cadastrado.
                            </Typography>
                          )}
                          <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3 mt-4">
                            Categorias
                          </h4>
                          <hr />
                          <ul className="font-xssss fw-500 mt-1 lh-3 list-unstyled">
                            {torneio?.categoriasTorneio?.map((cat, index) => {
                              const selected = selectedCategories.find((c) => c.categoria.id === cat.id);
                              const inscricao = inscricoes.find((i) => i.categoriaTorneio?.id === cat.id);
                              const partnerName = cat.numeroAtletas === 2 && inscricao
                                ? inscricao.equipe.atletas.find((a) => a.id !== atletaId)?.usuario.nome
                                : undefined;
                              const vagasInfo = getVagasInfo(cat.id, cat.vagas, contagemVagas, torneio?.filaEspera, cat.configFilaEspera);
                              const isVagasFull = vagasInfo.status === 'full';
                              const showSelectionControls = !inscricao && !isAdmin && !isVagasFull;
                              const showPartnerSelection = showSelectionControls && cat.numeroAtletas === 2;
                              const labelClassName = `form-check-label ${showSelectionControls ? 'ms-2 ' : ''}me-3`;
                              // Phase 4: regra de fila de espera por categoria
                              const inscricaoResult = podeSelecionarCategoria(cat, torneio || {});
                              const checkboxDisabled = !inscricaoResult.permitido;
                              const tooltipMotivo = checkboxDisabled ? (inscricaoResult.motivo || '') : '';
                              return (
                                <div
                                  className={`form-check mb-2 d-flex align-items-center`}
                                  style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#FFEFF4' }}
                                  key={cat.id}
                                >
                                  {showSelectionControls && (
                                    <span title={tooltipMotivo} style={{ display: 'inline-flex' }}>
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={cat.id}
                                        name="categorias"
                                        checked={!!selected}
                                        disabled={checkboxDisabled}
                                        onChange={(e) => handleCategoryChange(cat, e.target.checked)}
                                      />
                                    </span>
                                  )}
                                  <label className={labelClassName} htmlFor={cat.id}>
                                    <strong>{cat.nome}:</strong> {cat.precoInscricao ? `R$ ${cat.precoInscricao.toFixed(2)}` : 'Gratuito'}
                                  </label>
                                  {!inscricao && vagasInfo.status === 'available' && vagasInfo.vagasDisponiveis != null && (
                                    <span className="ms-2 text-muted font-xssss">
                                      ({vagasInfo.vagasDisponiveis} {vagasInfo.vagasDisponiveis === 1 ? 'vaga' : 'vagas'})
                                    </span>
                                  )}
                                  {!inscricao && vagasInfo.status === 'waitlist' && torneio?.exibirFilaEspera && (
                                    <span className="ms-2 text-info font-xssss fw-600">
                                      (Fila de espera)
                                    </span>
                                  )}
                                  {!inscricao && vagasInfo.status === 'full' && (
                                    <span className="ms-2 text-danger font-xssss fw-600">
                                      (Vagas esgotadas)
                                    </span>
                                  )}
                                  {showPartnerSelection && (
                                    <>
                                      <button
                                        id={`select-dupla-button-${cat.id}`}
                                        type="button"
                                        className="btn btn-outline-primary btn-sm ms-auto bg-primary text-white"
                                        style={{ borderRadius: "6px" }}
                                        onClick={() => openPartnerModal(cat)}
                                        disabled={!selected}
                                      >
                                        {selected?.partner ? 'TROCAR PARCEIRO' : 'SELECIONAR DUPLA'}
                                      </button>
                                      {selected?.partner && (
                                        <PartnerChip
                                          partner={selected.partner}
                                          onRemove={() => removePartner(cat.id)}
                                        />
                                      )}
                                    </>
                                  )}
                                  {inscricao && (
                                    cat.numeroAtletas === 2 ? (
                                      <span className="ms-auto fw-600 text-success">
                                        Inscrito{partnerName ? ` - Dupla: ${partnerName}` : ''}
                                      </span>
                                    ) : (
                                      <Chip
                                        label="INSCRITO"
                                        color="info"
                                        size="small"
                                        className="mx-auto"
                                      />
                                    )
                                  )}
                                </div>
                              );
                            })}
                          </ul>
                          {!isAdmin && (() => {
                            const descontos = (torneio?.descontoCategorias ?? []) as unknown as DescontoCategoriaCalc[];
                            const selecionadas: CategoriaSelecionadaCalc[] = selectedCategories.map(({ categoria }) => ({
                              id: categoria.id,
                              nome: categoria.nome,
                              precoInscricao: categoria.precoInscricao ?? 0,
                              aplicarDescontoTorneio: Boolean((categoria as any).aplicarDescontoTorneio),
                            }));
                            const totals = computeInscriptionTotals(selecionadas, descontos, priorEligibleCount);
                            return (
                              <div className="d-flex flex-column align-items-end mt-2">
                                <div className="form-check mb-2 d-flex align-items-center">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    id="agree-regulation"
                                    checked={regulamentoAceito}
                                    onChange={(e) => setRegulamentoAceito(e.target.checked)}
                                  />
                                  <label className="form-check-label text-end" htmlFor="agree-regulation">
                                    Estou de acordo com o regulamento do torneio.
                                  </label>
                                </div>
                                <div className="d-flex align-items-center">
                                  <button
                                    type="button"
                                    className="btn d-flex align-items-center px-4 py-2 btn-primary text-white"
                                    style={{ border: 'none', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                  >
                                    <i className="bi bi-pencil-square me-2" style={{ fontSize: '1.4rem' }}></i>
                                    <span className="fw-600 ">INSCREVER-SE</span>
                                  </button>
                                  <span className="ms-3 fw-600">Total: R$ {totals.valorComDesconto.toFixed(2)}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <RegulamentoModal
        open={regulamentoOpen}
        onClose={() => setRegulamentoOpen(false)}
        regulamentoHtml={regulamentoHtml}
      />

      <SelectPartnerModal
        open={selectPartnerOpen}
        onClose={closePartnerModal}
        recentPartners={[]}
        onSelect={handleSelectPartner}
      />

      <Dialog
        open={conflitoDialogOpen}
        onClose={() => setConflitoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Categorias não podem ser combinadas</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Não é possível seguir com esta inscrição:
          </Typography>
          <ul>
            {conflitosCategoriaLiga.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
          <Typography variant="body2" color="text.secondary">
            Ajuste a seleção para continuar. Se você acredita que estas categorias deveriam
            poder ser disputadas juntas, procure a organização da liga.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflitoDialogOpen(false)} variant="contained">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waitlistDialogOpen} onClose={() => setWaitlistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fila de Espera</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            As seguintes categorias estão com vagas esgotadas e sua inscrição ficará na fila de espera:
          </Typography>
          <ul>
            {waitlistCategories.map((nome) => (
              <li key={nome}><strong>{nome}</strong></li>
            ))}
          </ul>
          <Typography variant="body2" color="text.secondary">
            Deseja continuar com a inscrição?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitlistDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              setWaitlistDialogOpen(false);
              proceedToCheckout();
            }}
            variant="contained"
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Appfooter />
    </Fragment>
  );
};
export default Tournamentsingle;






